import React from 'react';
import { GridActionsCellItem } from '@mui/x-data-grid-premium';
import { Popover } from '@airbus/components-react';
import { Close, ErrorOutline } from '@airbus/icons/react';
import { ClickAwayListener } from '@mui/material';
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium';
import styles from './RowErrorAction.module.css';
import { t } from 'i18next';
import { useLanguageContextAPI } from '../../context/LanguageContext';

export function RowErrorAction({
  params,
  matchedItem,
  apiRef,
  columns,
}: {
  params: any;
  matchedItem: {
    rowno: string;
    column: Record<string, { field: string; msg: string }>;
  };
  apiRef: React.RefObject<GridApiPremium>;
  columns: any[];
}) {
  const [btnNode, setBtnNode] = React.useState<HTMLElement | null>(null);
  const [open, setOpen] = React.useState(false);
  const { language } = useLanguageContextAPI();

  const handleClickPopover = (event: React.MouseEvent<HTMLElement>) => {
    setBtnNode(event.currentTarget);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const scrollToRowAndHighlight = (rowId: string, field: string) => {
    const api = apiRef.current;
    if (!api) return;

    const colIndex = api.getVisibleColumns().findIndex((col) => col.field === field);

    const tryScroll = () => {
      const rowIndex = api.getRowIndexRelativeToVisibleRows(rowId);
      if (rowIndex === -1 || colIndex === -1) {
        setTimeout(tryScroll, 200);
        return;
      }

      api.scrollToIndexes({ rowIndex, colIndex });
      api.setCellFocus(rowId, field);

      setTimeout(() => {
        api.startCellEditMode({ id: rowId, field });

        const cell = document.querySelector<HTMLElement>(
          `.MuiDataGrid-cell[data-id="${rowId}"][data-field="${field}"]`,
        );
        if (cell) {
          cell.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
          const input = cell.querySelector('input');
          input?.focus();
          input?.select();
        }
      }, 200);
    };

    tryScroll();
  };

  const gridActionScroll = (params: any, key: string) => {
    const { id } = params;
    scrollToRowAndHighlight(id, key);
  };

  const getLocalizedColumnName = (col) => {
    return (
      columns
        .map((c) => {
          if (c.attrDisplayNameen === col?.field || c.attrDisplayNamede === col?.field) {
            return language === 'de' ? c.attrDisplayNamede : c.attrDisplayNameen;
          }
          return '';
        })
        .filter(Boolean)[0] || ''
    );
  };

  return (
    <>
      <GridActionsCellItem
        icon={<ErrorOutline style={{ fontSize: 20, color: 'red' }} />}
        label='Restore'
        onClick={handleClickPopover}
        showInMenu={false}
      />
      <Popover
        referenceNode={btnNode}
        open={open}
        placement='bottom-start'
        arrowClassName='custom-popover-arrow'
        className={styles.poopover}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <div className={styles.popoverContainer}>
            <div className={styles.header}>
              <div className={styles.headerText}>
                {Object.keys(matchedItem?.column)?.length > 0
                  ? Object.keys(matchedItem?.column)?.length > 1
                    ? `${Object.keys(matchedItem?.column)?.length} ${t('rowErrorChecking.errors_in_this_row')}`
                    : `${Object.keys(matchedItem?.column)?.length} ${t('rowErrorChecking.error_in_this_row')}`
                  : ''}
              </div>
              <button onClick={handleClose} className={styles.closeButtonDiv} aria-label='Close'>
                <Close className={styles.closeButton} />
              </button>
            </div>

            <div className={styles.errorList}>
              {Object.entries(matchedItem.column).map(([key, col], index) => (
                <div key={index} className={styles.errorItem}>
                  <div
                    className={styles.errorText}
                    onClick={() => {
                      handleClose();
                      gridActionScroll(params, key);
                    }}
                  >
                    {col?.msg.toLowerCase() === 'required' ? (
                      <>
                        • {getLocalizedColumnName(col)} {t('rowErrorChecking.is')}{' '}
                        {t('rowErrorChecking.required')}
                      </>
                    ) : (
                      <>{col?.msg}</>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </ClickAwayListener>
      </Popover>
    </>
  );
}
